import { IMongoDocument } from "utils/types"

export interface IClan extends IMongoDocument {
  avatar: string
  avatarApproved: boolean
  ownerId: string
  name: string
  members: number
  membersLimit: number
  minPowerToEnter?: number
  abbreviation: string
  description: string
  power: number
  improvements: number
  speedBonus: number
  staminaBonus: number
  luckBonus: number
  intuitionBonus: number
  mineRewardBonus: number
  place?: number
  closed: boolean
  balance: IClanBalance
  influence: number
  chatRoomId: string
}

export type IClanShort = Pick<IClan, "_id" | "abbreviation">

export interface IClanRequest {
  _id: string
  clanId: string
  message: string
  user: {
    _id: string
    name: string
    dogname: string
    visibleUsername: string
    visibleAvatar: string
    currentDiggingSpeed: number
    hasVipPass: boolean
  }
}

export interface IClanTransaction extends IMongoDocument {
  member: {
    _id: string
    visibleUsername: string
    visibleAvatar: string | null
    dogname: string
    name: string
    hasVipPass: boolean
  }
  bones: number
  gold: number
}

export interface IGetTransactionsDto {
  clanId: string
  userName: string
  page: number
  limit: number
  startedAt?: string
  mode?: string
}

export interface IUserClan extends IMongoDocument {
  userId: string
  clanId: string
  role: CLAN_ROLE
  clan: IClan
  leavedClanAt: string
}

export interface ICreateClanDto {
  name: string
  abbreviation: string
  description?: string
  minPowerToEnter?: number
  avatarId: string
  closed: boolean
  avatar: File | null
}

export interface IBuildingDto {
  clanId: string
  type: CLAN_BUILDING_TYPE
}

export interface IUpdateClanDto {
  clanId: string
  name?: string
  abbreviation?: string
  description?: string
  minPowerToEnter?: number
  closed?: boolean
  avatarId?: string
  file: File | null
}

export enum CLAN_USER_BALANCE {
  GOLD = "gold",
  BONES = "bones",
  LABR = "labr",
  STARS = "stars"
}

export interface IClanBalance {
  gold: number
  bones: number
  labr: number
  stars: number
  bl: number
}

export interface IDonateDto {
  currency: CLAN_USER_BALANCE
  value: number
}

export type LeaderBoardSort = "power" | "influence" | "members"

export interface IFilters {
  search?: string
  page?: number
  limit?: number
  sortKey?: LeaderBoardSort
}

export interface ILeaderBoardFilters {
  clanId: string
  sortKey?: LeaderBoardSort
}

export enum CLAN_ROLE {
  OWNER = "owner",
  MODERATOR = "moderator",
  HELPER = "helper",
  MEMBER = "member"
}

export interface IMember extends IMongoDocument {
  clanId: string
  role: CLAN_ROLE
  attachedGuildAt: Date
  user?: {
    _id: string
    name: string
    dogname: string
    visibleUsername: string
    visibleAvatar: string
    currentDiggingSpeed: number
    currentEndurance: number
    currentLuck: number
    power: number
    hasVipPass: boolean
  }
}

export interface IMemberFilters extends IFilters {
  clanId: string
  page?: number
  limit?: number
}

export interface IRequestsFilters extends IFilters {
  clanId?: string
  page?: number
  limit?: number
}

export enum AvatarType {
  CLAN_DEFAULT = "clan-default-avatar"
}

export interface IClanAvatar {
  _id: string
  src: string
  name: string
  type: AvatarType
}

export interface IChangeRoleDto {
  targetUserId: string
  newRole: CLAN_ROLE
}

export interface IClanTransferDto {
  clanId: string
  candidateId: string
}

export interface IExcludeMemberDto {
  clanId: string
  candidateToExcludeId: string
}

export interface IEnterClanDto {
  clanId: string
  message: string
}

export enum CLAN_BUILDING_TYPE {
  HUT = "hut",
  BANK = "bank",
  GYM = "gym",
  ARCADE = "arcade",
  STADIUM = "stadium",
  ALTAR = "altar",
  ARENA = "arena",
  WORKSHOP = "workshop",
  MINE = "mine"
}

export interface IClanBuildingInstance {
  _id: string
  type: CLAN_BUILDING_TYPE
  clanId: string
  name: string
  description: string
  level: number
  imgSrc: string
}

export interface IClanBuilding {
  type: CLAN_BUILDING_TYPE
  name: string
  description: string
  minLevel: number
  maxLevel: number
  imgSrc: string

  bonusesByLevel: {
    [level: string]: any
  }
}

export interface IGetUpgradeDto {
  level: number
  type: CLAN_BUILDING_TYPE
}

export interface IBuildingNextUpgradeDto {
  upgradeAvailable: boolean
  updatePrice: {
    currency: CLAN_USER_BALANCE
    value: number
  }[]
}

export interface ITaxCurrency {
  bones: number
  gold: number
  labr: number
  stars: number
}

export interface IClanTax {
  _id: string
  attachedClanAt: string
  availableToClaim: ITaxCurrency
  clanId: string
  role: CLAN_ROLE
  totalClaimed: ITaxCurrency
  user: {
    name: string
    dogname: string
    visibleUsername: string
    visibleAvatar: string
    hasVipPass: boolean
  }
}

export interface IGetTaxesDto {
  clanId: string
  page?: number
  limit?: number
}

export interface IActivityLogsDto {
  type: string
  clanId: string
  page: number
  limit: number
}

export enum ACTIVITY_LOG_TYPE {
  BUILDING = "building",
  MEMBER_EXCLUDED = "member-excluded",
  NEW_MEMBER = "new-member",
  MEMBER_LEAVED = "member-leaved",
  CLAN_CREATED = "clan-created",
  CLAN_TRANSFERRED = "clan-transferred",
  ALTAR_DONATE = "altar-donate"
}

export interface IActivityLog extends IMongoDocument {
  clanId: string
  type: ACTIVITY_LOG_TYPE
  name: string
  description: string
  data: Record<string, any>
}
